/**
 * The investors a founder is allowed to see, and the one they are looking at.
 *
 * Investors are NOT a browsable directory — `investor_profiles` lost its
 * blanket public read in the E-11 fix, deliberately. A founder sees an
 * investor because that investor engaged with them: opened a conversation,
 * asked for the data room, ran a due diligence, or took a position.
 *
 * `get_related_investors()` returns exactly that set, already filtered
 * server-side, and says WHY each investor is in it. The reason matters on
 * screen: "asked for your data room" and "is on your cap table" are very
 * different relationships to be shown the same way.
 */

import { useQuery } from '@tanstack/react-query';

import { queryKeys } from '@/lib/supabase/api';
import { isSupabaseConfigured, supabase } from '@/lib/supabase/client';
import { rpc } from '@/lib/supabase/rpc';
import { mapInvestorProfileRow } from '@/lib/supabase/profile-mappers';
import type { InvestorProfile } from '@/lib/state/profile-store';
import type { InvestorRelationship } from '@/lib/investor/investor-relationship';
import {
  asAuthUserId,
  asInvestorProfileId,
  type AuthUserId,
  type InvestorProfileId,
} from '@/lib/ids';


export interface RelatedInvestor {
  /** investor_profiles.id — what the detail route takes. */
  profileId: InvestorProfileId;
  /** auth.users.id — what the reviews and metrics RPCs take. */
  userId: AuthUserId;
  name: string;
  firm: string | null;
  headline: string | null;
  location: string | null;
  investmentFocus: string[];
  stagePreference: string[];
  ticketSizeMin: number;
  ticketSizeMax: number;
  linkedInVerified: boolean;
  relationship: InvestorRelationship;
}


/** Investors who have engaged with a startup the caller is an active member of. */
export function useRelatedInvestors() {
  return useQuery({
    queryKey: queryKeys.relatedInvestors(),
    enabled: isSupabaseConfigured(),
    queryFn: async (): Promise<RelatedInvestor[]> => {
      const { data, error } = await rpc('get_related_investors');
      if (error) throw error;

      return (data ?? []).map((r) => ({
        // Boundary: the RPC returns plain strings; the id spaces are known
        // from its signature, so assert them here rather than downstream.
        profileId: asInvestorProfileId(r.investor_profile_id),
        userId: asAuthUserId(r.investor_user_id),
        name: r.name,
        firm: r.firm ?? null,
        headline: r.headline ?? null,
        location: r.location ?? null,
        investmentFocus: r.investment_focus ?? [],
        stagePreference: r.stage_preference ?? [],
        ticketSizeMin: r.ticket_size_min ?? 0,
        ticketSizeMax: r.ticket_size_max ?? 0,
        linkedInVerified: r.linkedin_verified ?? false,
        relationship: r.relationship as InvestorRelationship,
      }));
    },
  });
}

export interface InvestorPublicProfile {
  profile: InvestorProfile;
  /** auth.users.id — the key for reviews, metrics and track record. */
  userId: AuthUserId;
  name: string;
}

/**
 * One investor's full profile.
 *
 * A plain table read: RLS decides whether the caller may see it, so an
 * unrelated founder gets no row rather than a filtered one. `null` therefore
 * means "not visible to you", which the screen renders as a refusal rather
 * than a spinner that never resolves.
 */
export function useInvestorPublicProfile(profileId: string | undefined) {
  return useQuery({
    queryKey: queryKeys.investorPublicProfile(profileId ?? ''),
    enabled: !!profileId && isSupabaseConfigured(),
    queryFn: async (): Promise<InvestorPublicProfile | null> => {
      if (!profileId) return null;

      const { data, error } = await supabase
        .from('investor_profiles')
        .select('*')
        .eq('id', profileId)
        .maybeSingle();
      if (error) throw error;
      if (!data) return null;

      return {
        profile: mapInvestorProfileRow(data),
        userId: asAuthUserId(data.user_id),
        name: data.name,
      };
    },
  });
}

// Re-exported so callers need a single import for the list and its labels.
export { RELATIONSHIP_LABELS, INVESTOR_RELATIONSHIPS } from '@/lib/investor/investor-relationship';
export type { InvestorRelationship } from '@/lib/investor/investor-relationship';
